import React, { useState } from "react";

import "./ConnectorForm.css";

import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import Textarea from "../../components/FormComponents/Textarea";
import { useAxiosContext } from "../../context/AxiosContext";
import { useParams } from "react-router-dom";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import { useDBConnectorsContext } from "../../context/DBConnectorContext";

const resources = [
  { name: "MySQL", id: "mysql", type: "zino" },
  { name: "PostgreSQL", id: "postgresql", type: "zino" }
];

const providers = [
  { id: "zino", name: "Zino" },
  { id: "custom", name: "BYOD" }
];

const ConnectorForm = ({ handleClose }) => {
  const { setExplorerData, setSelectedConnector, explorerData } = useDBConnectorsContext();

  const [formData, setFormData] = useState({
    name: "",
    database: "",
    description: "",
    provider: "",
    database_type: "MySQL",
    version: "1",
    host: "",
    port: null, // 👈 INTEGER
    users: [],
    schema_name: ""
  });

  const [selectedId, setSelectedId] = useState("mysql");
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState(null);

  const { axiosInstance, handleError } = useAxiosContext();
  const { appId } = useParams();

  // ===============================
  // CHANGE HANDLER (PORT AS INT)
  // ===============================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "port" ? (value === "" ? null : parseInt(value, 10)) : value
    }));

    setError(null);
  };

  const handleSelect = (item) => {
    setSelectedId(item.id);
    setFormData((prev) => ({
      ...prev,
      database_type: item.name
    }));
    setError(null);
  };

  const handleProviderChange = (provider) => {
    setProvider(provider.id);
    setFormData((prev) => ({
      ...prev,
      provider: provider.id,
      database: provider.id === "zino" ? `data_${appId}` : "",
      host: "",
      port: null
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!provider) errors.provider = "Select a provider";
    if (!selectedId) errors.databaseType = "Select a Database Type";

    if (provider) {
      if (!formData.name) errors.name = "Name is required";

      if (provider === "custom") {
        if (formData.port === null || formData.port < 1 || formData.port > 65535) {
          errors.port = "Enter a valid port (1–65535)";
        }
        if (!formData.host) errors.host = "Host is required";
      }

      if (!formData.database) errors.database = "Database is required";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    try {
      console.log("Submitting form data:", formData);

      const { data } = await axiosInstance.post("/dbconnector", formData);

      const transformedData = {
        id: data.id,
        name: data?.name || "",
        parentData: "connectors",
        isFolder: false,
        items: [],
        can_delete: true,
        can_edit: false,
        type: data?.database_type || "",
        fileInfo: {}
      };

      setExplorerData([
        {
          ...explorerData[0],
          items: [...explorerData[0].items, transformedData]
        }
      ]);

      setSelectedConnector(transformedData);
      handleClose();
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <div className="flex_column gap_3">
      <SingleSelectBox
        selectedData={provider}
        placeholder="Select a Provider"
        options={providers}
        onChange={handleProviderChange}
        error={error?.provider}
      />

      <div className="d_flex gap_3">
        {provider &&
          resources.map((item) => (
            <p
              key={item.id}
              className={`resource_card ${selectedId === item.id ? "active" : ""}`}
              onClick={() => handleSelect(item)}
            >
              {item.name}
            </p>
          ))}
      </div>

      <ErrorMessage>{error?.databaseType}</ErrorMessage>

      {provider && getCustomForm(provider, formData, handleChange, error)}

      <Button onClick={handleSubmit} className="cursor_pointer">
        Create
      </Button>
    </div>
  );
};

export default ConnectorForm;

// ==================================
// CUSTOM FORM
// ==================================
const getCustomForm = (provider, formData, handleChange, error) => {
  const isCustom = provider === "custom";

  return (
    <div>
      <Input
        name="name"
        containerClassName="mb_3"
        label="DBConnector Name"
        placeholder="e.g. Prod-Analytics-DB"
        value={formData.name}
        onChange={handleChange}
        error={error?.name}
      />

      {isCustom && (
        <>
          <Input
            name="port"
            containerClassName="mb_3"
            label="Port"
            type="number"
            min={1}
            max={65535}
            placeholder="e.g. 5432"
            value={formData.port ?? ""}
            onChange={handleChange}
            error={error?.port}
          />

          <Input
            name="host"
            containerClassName="mb_3"
            label="Host"
            placeholder="e.g. 127.0.0.1 or db.example.com"
            value={formData.host}
            onChange={handleChange}
            error={error?.host}
          />
        </>
      )}

      <Input
        name="database"
        containerClassName="mb_3"
        label="Database Name"
        placeholder="e.g. my_database"
        value={formData.database}
        onChange={handleChange}
        disabled={!isCustom}
        error={error?.database}
      />

      {isCustom && (
        <Input
          name="schema_name"
          containerClassName="mb_3"
          label="Schema"
          placeholder="public"
          value={formData.schema_name}
          onChange={handleChange}
          error={error?.schema_name}
        />
      )}

      <Textarea
        name="description"
        containerClassName="mb_3"
        label="Description"
        placeholder="Describe the purpose of this connection..."
        value={formData.description}
        onChange={handleChange}
      />
    </div>
  );
};
